import { RpNoveltyIntensityCountsResponse } from '@graphql/queries/RpNoveltyIntensityCounts';
import { RpProductTimeSeriesCountsResponse } from '@graphql/queries/RpProductTimeSeriesCounts';
import { formatReportLabel } from 'containers/Project/utils';

import { ProjectSummaryQaPreview } from './types';
import { OPTIMIZATION } from '../../constants/report';
import { textureViewerRole } from '../../constants/userRoles';
import { ReportSummaryResponse } from '../../hooks/useReportSummary';
import {
  CustomerPreferences,
  dataDownloadEnabled,
  mapEnabled,
  normsEnabled,
} from '../../services/customerPreferences';
import { ProjectTabs } from '../../services/customerPreferences';
import { isAfsWorkspace, isDemoWorkspace } from '../../utils/afsUtils';
import { hasAdminRole, hasSuperAdminRole } from '../../utils/roleUtils';

/**
 * Show original products toggle.  This toggle only makes sense if it is a optimization report
 * and the parent report is included in the project
 *
 * @param reportId child report id
 * @param parentReportId parent report id
 * @param data reportSummaryResponse
 * @returns if toggle should show
 */
export const showOriginalProductsToggle = (
  reportId: string,
  parentReportId: string,
  data: ReportSummaryResponse,
) => {
  const reportType = data.allReportJobs.nodes.find(
    (r) => r.reportId === reportId,
  )?.reportType;
  return !!parentReportId && reportType === OPTIMIZATION;
};

/**
 * Show product summary only if Map Only was approved.
 *
 * @param reportId child report id
 * @param data reportSummaryResponse
 * @returns if tabs should show
 */
export const showMapOnly = (reportId: string, data: ReportSummaryResponse) => {
  return data.allReportJobs.nodes.find((r) => r.reportId === reportId)?.mapOnly;
};

/**
 * Show flow summary tabs on project. Only if flavor profiles are in database
 *
 * @param marketMapOnly only show market map
 * @param reportFlavorCnt count of flavor profiles
 * @returns if flavor summary tabs should show
 */
export const showFlavor = (
  marketMapOnly: boolean,
  reportFlavorCnt: reports.ReportFlavorCountsResponse,
): boolean =>
  !marketMapOnly &&
  (reportFlavorCnt?.reportJobByReportId?.rpGgvarFlavorProfilesByReportId
    ?.totalCount ?? 0) > 0;

/**
 * Show texture tabs on project
 *
 * @param userRoles user roles for current user
 * @param marketMapOnly only show market map
 * @param reportTextureCnt number of texture rows in database
 * @returns if texture tab should show
 */
export const showTexture = (
  userRoles: Set<string>,
  marketMapOnly: boolean,
  reportTextureCnt: reports.ReportTextureCountResponse,
): boolean =>
  userRoles &&
  !marketMapOnly &&
  (hasSuperAdminRole(userRoles) ||
    hasAdminRole(userRoles) ||
    userRoles.has(textureViewerRole.toLowerCase())) &&
  (reportTextureCnt?.reportJobByReportId?.rpTextureClustersByReportId
    ?.totalCount ?? 0) > 0;

/**
 * Show data download tabs on project
 *
 * @param customerPreferences relevant customer preferences object
 * @param userRoles relevant user roles
 * @param marketMapOnly only show market map
 * @returns if data download tab should show
 */
export const showDataDownload = (
  customerPreferences: CustomerPreferences,
  userRoles: Set<string>,
  marketMapOnly: boolean,
): boolean =>
  !!customerPreferences &&
  !!userRoles &&
  !marketMapOnly &&
  (dataDownloadEnabled(customerPreferences) || hasSuperAdminRole(userRoles));

/**
 * Show norms tabs on project
 *
 * @param customerPreferences relevant customer preferences object
 * @param userRoles relevant user roles
 * @param marketMapOnly only show market map
 * @returns if norms tab should show
 */
export const showNorms = (
  customerPreferences: CustomerPreferences,
  userRoles: Set<string>,
  marketMapOnly: boolean,
): boolean =>
  !!customerPreferences &&
  !!userRoles &&
  !marketMapOnly &&
  normsEnabled(customerPreferences) &&
  hasSuperAdminRole(userRoles);

/**
 * Show optimziation summary / order of operations tab on project
 *
 * @param isProd boolean for if current environment is prod
 * @param marketMapOnly only show market map
 * @param selectedReportType current report type
 * @param optSummaryCnt number of order of operations rows in database
 * @returns if optimization summary tab should show
 */
export const showOptimizationSummary = (
  isProd: boolean,
  marketMapOnly: boolean,
  selectedReportType: string,
  optSummaryCnt: reports.ReportOrderOfOperationsCountResponse,
): boolean =>
  !marketMapOnly &&
  selectedReportType === OPTIMIZATION &&
  (optSummaryCnt?.allRpOptSteps?.totalCount ?? 0) > 0;

/**
 * Show map tabs on project
 *
 * @param customerPreferences relevant customer preferences object
 * @param marketMapOnly only show market map
 * @returns if map tab should show
 */
export const showMap = (
  customerPreferences: CustomerPreferences,
  marketMapOnly: boolean,
): boolean =>
  !marketMapOnly && !!customerPreferences && mapEnabled(customerPreferences);

/**
 * Show novelty analysis tab on project
 *
 * @param marketMapOnly only show market map
 * @param workspaceId current workspace for project
 * @param noveltyCnt number of novelty rows in the database
 * @returns if novelty analysis tab should show
 */
export const showNoveltyAnalysis = (
  marketMapOnly: boolean,
  workspaceId: number,
  noveltyCnt: RpNoveltyIntensityCountsResponse,
): boolean =>
  !marketMapOnly &&
  (isAfsWorkspace(workspaceId) || isDemoWorkspace(workspaceId)) &&
  (noveltyCnt?.allRpNoveltyIntensityPqs.totalCount ?? 0) > 0;

/**
 * Show executive summary tab on preojct
 *
 * @param marketMapOnly only show market map
 * @returns if executive summary tab should show
 */
export const showExecutiveSummary = (marketMapOnly: boolean): boolean =>
  !marketMapOnly;

/**
 * Show trend analysis tabs on project
 *
 * @param customerPreferences relevant customer preferences object
 * @param marketMapOnly only show market map
 * @param trendAnalysisCnt number of trend analysis rows in database
 * @returns if trend analysis tab should show
 */
export const showTrendAnalysis = (
  customerPreferences: CustomerPreferences,
  marketMapOnly: boolean,
  trendAnalysisCnt: RpProductTimeSeriesCountsResponse,
): boolean =>
  customerPreferences.features.showTrendAnalysis &&
  !marketMapOnly &&
  (trendAnalysisCnt?.allRpProductTimeseries.totalCount ?? 0) > 0;

/**
 * Show category insights tab on project
 *
 * @param isProd boolean for if current environment is prod
 * @returns if category insights tab should show
 */
export const showCategoryInsights = (isProd: boolean): boolean => !isProd;

export const showTab = (
  tab: ProjectTabs,
  customerPreferences: CustomerPreferences,
  userRoles: Set<string>,
  marketMapOnly: boolean,
  workspaceId: number,
  selectedReportType: string,
  reportFlavorCnt: reports.ReportFlavorCountsResponse,
  reportTextureCnt: reports.ReportTextureCountResponse,
  noveltyCnt: RpNoveltyIntensityCountsResponse,
  trendAnalysisCnt: RpProductTimeSeriesCountsResponse,
  optSummaryCnt: reports.ReportOrderOfOperationsCountResponse,
  isProd: boolean,
): boolean => {
  switch (tab) {
    case ProjectTabs.EXECUTIVE_SUMMARY:
      return showExecutiveSummary(marketMapOnly);
    case ProjectTabs.PROJECT_SUMMARY:
      return true;
    case ProjectTabs.FLAVOR_SUMMARY:
      return showFlavor(marketMapOnly, reportFlavorCnt);
    case ProjectTabs.TEXTURE_SUMMARY:
      return showTexture(userRoles, marketMapOnly, reportTextureCnt);
    case ProjectTabs.OPTIMIZATION_SUMMARY:
      return showOptimizationSummary(
        isProd,
        marketMapOnly,
        selectedReportType,
        optSummaryCnt,
      );
    case ProjectTabs.NORMS:
      return showNorms(customerPreferences, userRoles, marketMapOnly);
    case ProjectTabs.DATA_DOWNLOAD:
      return showDataDownload(customerPreferences, userRoles, marketMapOnly);
    case ProjectTabs.PROJECT_MAP:
      return showMap(customerPreferences, marketMapOnly);
    case ProjectTabs.NOVELTY_ANALYSIS:
      return showNoveltyAnalysis(marketMapOnly, workspaceId, noveltyCnt);
    case ProjectTabs.TREND_ANALYSIS:
      return showTrendAnalysis(
        customerPreferences,
        marketMapOnly,
        trendAnalysisCnt,
      );
    case ProjectTabs.CATEGORY_INSIGHTS:
      return showCategoryInsights(isProd);
  }
};

const reportJobLabel = (reportJob: reports.ReportJob): string =>
  formatReportLabel(
    reportJob.projectName,
    reportJob.targetGroupName,
    reportJob.reportType,
    reportJob.startedAt,
  );

const toProjectReportSummary = (
  reportJob: reports.ReportJob,
  labelSuffix = '',
): reports.ProjectReportSummary => ({
  reportId: reportJob.reportId,
  reportType: reportJob.reportType,
  clientName: reportJob.clientName,
  passedQa: reportJob.passedQa,
  projectName: reportJob.projectName,
  startedAt: reportJob.startedAt,
  pdfEfsUri: reportJob.pdfEfsUri,
  jobId: reportJob.jobId,
  parentJobId: reportJob.parentJobId,
  targetGroupName: reportJob.targetGroupName,
  name: `${reportJobLabel(reportJob)}${labelSuffix}`,
});

const toReportProductInfo = (
  reportJob: reports.ReportJob,
  customNames: reports.CustomProductNamesDetailRow[],
  labelSuffix = '',
): reports.ReportProductInfo => ({
  reportId: reportJob.reportId,
  reportType: reportJob.reportType,
  name: `${reportJobLabel(reportJob)}${labelSuffix}`,
  products:
    reportJob.reportSummaries?.nodes?.map((productItem) => {
      const customName = customNames.find(
        (f) => f.productId === productItem.productByProductId.id,
      );
      return {
        id: productItem.productByProductId.id,
        canonicalName: productItem.productByProductId.name,
        name: customName
          ? customName.productName
          : productItem.productByProductId.name,
        version: productItem.version,
      };
    }) ?? [],
});

export interface ProjectReportLists {
  projectReports: reports.ProjectReportSummary[];
  flavorReports: reports.ReportProductInfo[];
}

/**
 * Build the report lists shown in the Project Summary. Clients only ever see
 * reports that passed QA. When a QA preview report is supplied it is appended
 * (regardless of its QA status) so reviewers see it exactly where it would
 * appear once approved and added to the project.
 *
 * @param project project data, or null when previewing a report standalone
 * @param previewReport report under QA review, if any
 * @param previewLabelSuffix suffix that marks the preview report in the selector
 * @returns report lists for the summary selector and the flavor tab
 */
export const buildProjectReportLists = (
  project: reports.ProjectReport['project'] | null | undefined,
  previewReport?: reports.ReportJob | null,
  previewLabelSuffix = '',
): ProjectReportLists => {
  const customNames = project?.customPqProductNamesByProjectId?.nodes ?? [];
  const approvedJobs = (project?.projectReportsByProjectId?.nodes ?? [])
    .map((item) => item.reportJob)
    .filter(
      (reportJob) =>
        reportJob?.passedQa &&
        reportJob.reportId !== previewReport?.reportId,
    );

  const projectReports = approvedJobs.map((reportJob) =>
    toProjectReportSummary(reportJob),
  );
  const flavorReports = approvedJobs.map((reportJob) =>
    toReportProductInfo(reportJob, customNames),
  );

  if (previewReport) {
    projectReports.push(
      toProjectReportSummary(previewReport, previewLabelSuffix),
    );
    flavorReports.push(
      toReportProductInfo(previewReport, customNames, previewLabelSuffix),
    );
  }

  return { projectReports, flavorReports };
};

/**
 * In QA preview mode ProjectSummary loads the project chosen in the preview
 * ("0" = standalone, no project) instead of the one in the route.
 */
export const getQaPreviewContext = (
  qaPreview: ProjectSummaryQaPreview | undefined,
): {
  isQaPreview: boolean;
  isStandalonePreview: boolean;
  previewReportId: string | undefined;
  previewProjectId: string | undefined;
} => ({
  isQaPreview: !!qaPreview,
  isStandalonePreview: !!qaPreview && !qaPreview.projectId,
  previewReportId: qaPreview?.report.reportId,
  previewProjectId: qaPreview ? String(qaPreview.projectId ?? 0) : undefined,
});
